// One sentence saying what to do with this task now.
//
// The panel told its state three times — header chip, cancellation banner,
// scheduling chip — and its next step never. A front-desk person had to read
// three panels and infer. Every fact below is one the panel already computes
// for a caption somewhere; this only decides which matters most and says it.
//
// Precedence is the order in which a fact settles the question: a
// cancellation makes everything else moot; a booking makes a link moot; a
// live link is the thing in flight; then what the AI or the office has
// already sent; then what is merely chosen; then nothing.
import { panelHelperText } from './schedulingPanelCopy';

export interface NextStepFacts {
  status: string;
  offerCount: number;
  link: { heldCount: number; sentAtLabel: string | null; urlInReply: boolean } | null;
  booked: { label: string; toldAtLabel: string | null } | null;
  /** Times the AI picked in its draft, waiting to be taken or ignored. */
  suggested?: number;
  /** The AI drafted a booking confirmation for a time the patient chose. */
  confirming?: boolean;
}

const times = (n: number) => `${n} ${n === 1 ? 'time' : 'times'}`;

export function taskNextStep({ status, offerCount, link, booked, suggested = 0, confirming = false }: NextStepFacts): string {
  if (status === 'appointment_cancelled') {
    return 'Cancelled — offer new times below if they want one.';
  }
  if (status === 'appointment_confirmed') {
    if (!booked) return 'Booked — Complete when you are done, or Offer different times.';
    return booked.toldAtLabel
      ? `Booked ${booked.label} · patient told at ${booked.toldAtLabel} — Complete when you are done.`
      : `Booked ${booked.label} · patient not told yet — the confirmation is in your reply; press Send Response.`;
  }
  if (link) {
    const what = link.heldCount > 0 ? `${times(link.heldCount)} held` : 'Open link out';
    if (link.sentAtLabel) {
      return link.heldCount > 0
        ? `${what} · sent at ${link.sentAtLabel} — waiting for the patient to tap one.`
        : `${what} · sent at ${link.sentAtLabel} — waiting for the patient to pick a time.`;
    }
    return link.urlInReply
      ? `${what} · not sent yet — press Send Response.`
      : `${what} · not sent yet — Add to reply, then Send Response.`;
  }
  // The AI's picks are a suggestion, not an offer: they wait beside the
  // list until the office takes them. Once anything is chosen, the chosen
  // times lead — that is what Send will carry.
  if (status === 'pending_approval' && confirming) {
    return 'Written by AI and waiting in your reply box — read it, then Send Response.';
  }
  if (status === 'pending_approval' && offerCount === 0) {
    if (suggested === 0) return 'The AI found nothing open — pick times below, or write a reply.';
    return `The AI suggests ${times(suggested)} — use ${suggested === 1 ? 'it' : 'them'}, or pick your own below.`;
  }
  if (status === 'slots_sent') return 'Times sent — waiting for the patient to choose, or send more.';
  // Reached by a reply that offered no times — a "no availability" message,
  // or any plain answer sent while the AI's suggestion waited. Say what
  // happened, not what the patient was told.
  if (status === 'no_slots_available') return 'Replied with no times — pick times below when you are ready to offer some.';
  if (offerCount > 0) {
    const it = offerCount === 1 ? 'it' : 'them';
    return `${times(offerCount)} chosen — Send Response to offer ${it}, or Create a booking link to hold ${it}.`;
  }
  // The panel's existing wording for the states above that carry no richer
  // fact, so nothing already written and tested is lost.
  return panelHelperText(status);
}
